import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatError, formatPrice } from "../../lib/api";

const CARRIERS = { mondial_relay: "Mondial Relay", colissimo: "Colissimo", chronopost: "Chronopost" };

export default function AdminShipping({ order, onTracking }) {
  const [job, setJob] = useState(null);
  const [weight, setWeight] = useState(String(order.weight_g || ""));
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const base = `/admin/orders/${encodeURIComponent(order.id)}/shipping`;
  const realShipment = order.is_test === false && ["point_relais", "domicile"].includes(order.shipping?.method);
  const paid = order.payment_status === "paid";
  const active = paid && !["annulee", "remboursee", "livree"].includes(order.status);
  useEffect(() => {
    if (!realShipment) return;
    let mounted = true;
    const load = async () => {
      try { const { data } = await api.get(base); if (mounted) { setJob(data); setError(""); } }
      catch (e) { if (mounted) setError(formatError(e)); }
    };
    load();
    const timer = window.setInterval(load, 15000);
    return () => { mounted = false; window.clearInterval(timer); };
  }, [base, realShipment]);

  if (!realShipment) return null;
  const prepare = async () => {
    const grams = Number(weight);
    if (!Number.isInteger(grams) || grams < 1 || grams > 30000) { toast.error("Indiquez le poids du colis emballé, entre 1 g et 30 kg."); return; }
    setBusy(true); setConfirmed(false);
    try { const { data } = await api.post(`${base}/prepare`, { weight_g: grams }); setJob(data); setError(""); }
    catch (e) { setError(formatError(e)); }
    finally { setBusy(false); }
  };
  const createLabel = async () => {
    if (!window.confirm(`Créer ce bordereau facturé par Sendcloud ?\nTarif estimé : ${formatPrice(job.price_ht)} HT, plus TVA et éventuels suppléments.\nColis emballé : ${job.weight_g} g.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`${base}/label`, { quote_token: job.quote_token, packed_weight_confirmed: confirmed });
      setJob(data); setError("");
      onTracking?.(data);
      toast.success("Bordereau créé. Le colis reste à déposer au transporteur.");
    } catch (e) {
      setError(formatError(e));
      try { const { data } = await api.get(base); setJob(data); } catch { /* Keep the original error. */ }
    } finally { setBusy(false); }
  };
  const download = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`${base}/label.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a"); link.href = url; link.download = `bordereau-${order.order_number}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error("Le bordereau n'est pas encore disponible. Réessayez dans quelques instants."); }
    finally { setBusy(false); }
  };
  const pending = ["queued", "preparing", "purchasing"].includes(job?.state);
  const weightChanged = Number(weight) !== job?.weight_g;
  return <section className="border border-[#C9A45C]/30 p-4 space-y-4" data-testid="order-shipping-automation">
    <div><p className="eyebrow mb-2">Expédition Sendcloud</p><p role="status" className="text-sm text-[#E5C178]">{job?.message || (paid ? "Préparation de l'expédition" : "En attente du paiement")}</p></div>
    {error && <p className="text-sm text-red-300" role="alert">{error}</p>}
    {job?.state === "review_required" && <p className="text-xs text-[#F8F6F1]/60">Vérifiez la commande dans Sendcloud avant de poursuivre. La demande utilise une référence unique pour éviter les doublons.</p>}
    {job?.parcel_id ? <>
      <button type="button" onClick={download} disabled={busy} className="btn-gold w-full !py-3" data-testid="shipping-label-download">{busy ? "Chargement…" : "Télécharger le bordereau PDF A4"}</button>
      {(job.tracking_number || order.tracking_number) && <p className="text-xs">Suivi : {job.tracking_number || order.tracking_number}</p>}
      {(job.tracking_url || order.tracking_url)?.startsWith("https://") && <a href={job.tracking_url || order.tracking_url} target="_blank" rel="noopener noreferrer" className="text-[#C9A45C] underline">Suivre le colis</a>}
      <p className="text-xs text-[#F8F6F1]/55">{order.status === "expediee" ? "Le suivi est actualisé automatiquement depuis Sendcloud." : "Après le dépôt du colis, passez la commande en « Expédiée » pour prévenir le client."}</p>
    </> : active && job?.state !== "review_required" && <>
      <label className="block text-xs text-[#F8F6F1]/65">Poids du colis emballé (g)
        <input type="number" min="1" max="30000" step="1" value={weight} onChange={(e) => { setWeight(e.target.value); setConfirmed(false); }} disabled={busy || pending} className="mt-2 w-full bg-[#0B0B0B] border border-[#C9A45C]/25 px-3 py-2.5 text-sm" data-testid="shipping-packed-weight" />
      </label>
      {!pending && <button type="button" onClick={prepare} disabled={busy} className="btn-outline-gold !py-3 w-full" data-testid="shipping-prepare-button">{busy ? "Vérification…" : job?.state === "ready" ? "Actualiser le poids et le devis" : "Vérifier et préparer l'expédition"}</button>}
      {job?.state === "ready" && <div className="space-y-3">
        <p className="text-sm">{CARRIERS[job.carrier] || job.carrier} · {job.weight_g} g · <span className="text-[#C9A45C]">{formatPrice(job.price_ht)} HT estimés</span></p>
        <p className="text-xs text-[#F8F6F1]/55">{job.address_check === "relay_verified" ? "Relais et transporteur vérifiés auprès de Sendcloud." : "Les champs de l'adresse sont complets. Vérifiez l'adresse avant validation."} Tarif hors TVA et suppléments éventuels.</p>
        <label className="flex gap-2 items-start text-xs"><input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} disabled={weightChanged || busy} className="accent-[#C9A45C] mt-0.5" data-testid="shipping-confirm-packed-weight" />J'ai vérifié la destination et le poids réel du colis emballé.</label>
        {weightChanged && <p className="text-xs text-[#E5C178]">Actualisez le devis avec ce poids avant de valider.</p>}
        <button type="button" onClick={createLabel} disabled={busy || !confirmed || weightChanged} className="btn-gold !py-3 w-full disabled:opacity-40" data-testid="shipping-create-label">{busy ? "Création…" : "Valider et créer le bordereau"}</button>
      </div>}
      <p className="text-xs text-[#F8F6F1]/45">La préparation ne vous est pas facturée. Le bordereau est demandé uniquement après votre validation.</p>
    </>}
  </section>;
}
